'use client';

import { useCallback } from 'react';
import { Button } from '@/primitives';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import type { EbvConfigItem } from '@/hooks/publicSearch/ebv/ebvColumns';
import { csvEscape, formatEbv } from './helpers';
import { BREED_AVERAGE_FALLBACK_LABEL } from './constants';
import type { AnimalEbvRecord, MatingPredictorResponse } from '../types';

interface MatingExportCsvButtonProps {
  data: MatingPredictorResponse;
  visibleEbvs: EbvConfigItem[];
  sireMap: Map<number, AnimalEbvRecord>;
  damMap: Map<number, AnimalEbvRecord>;
  currencySymbol: string;
}

export default function MatingExportCsvButton({
  data,
  visibleEbvs,
  sireMap,
  damMap,
  currencySymbol,
}: MatingExportCsvButtonProps) {
  const handleExportCsv = useCallback(() => {
    const ebvHeaders = visibleEbvs.map((ebvConfig) =>
      ebvConfig.label.replace(/\^/g, ' '),
    );
    const inbreedingHeaders = [
      'Inbreeding %',
      'Min Generations',
      'Avg Generations',
    ];
    const header = [
      'Sire Name',
      'Sire Ident',
      'Dam Name',
      'Dam Ident',
      ...ebvHeaders,
      ...inbreedingHeaders,
    ];

    const emptyInbreeding = ['', '', ''];

    const rows: string[][] = [];

    for (const result of data.results) {
      const sire = sireMap.get(result.sireId);
      const dam = damMap.get(result.damId);
      if (!sire || !dam) continue;

      const projectedCells = visibleEbvs.map((ebvConfig) => {
        const ebvNumber = parseInt(ebvConfig.field.replace(/[^\d]/g, ''), 10);
        const projected = result.projectedEbvs.find(
          (candidate) => candidate.ebvNumber === ebvNumber,
        );
        return formatEbv(
          projected?.value ?? null,
          ebvConfig.format,
          currencySymbol,
        );
      });
      const inbreeding = result.inbreeding;
      const inbreedingCells = [
        inbreeding?.coefficientPercent ?? '',
        inbreeding?.minimumGenerations != null
          ? String(inbreeding.minimumGenerations)
          : '',
        inbreeding?.averageRelatedness != null
          ? String(inbreeding.averageRelatedness.toFixed(1))
          : '',
      ];
      rows.push([
        sire.animalName ?? '',
        sire.animalIdent ?? '',
        dam.animalName ?? '',
        dam.animalIdent ?? '',
        ...projectedCells,
        ...inbreedingCells,
      ]);
    }

    if (data.breedAverages.length > 0) {
      const breedAverageCells = visibleEbvs.map((ebvConfig) => {
        const ebvNumber = parseInt(ebvConfig.field.replace(/[^\d]/g, ''), 10);
        const breedAverage = data.breedAverages.find(
          (candidate) => candidate.ebvNumber === ebvNumber,
        );
        return formatEbv(
          breedAverage?.value ?? null,
          ebvConfig.format,
          currencySymbol,
        );
      });
      rows.push([
        '',
        '',
        data.breedAverages[0]?.label ?? BREED_AVERAGE_FALLBACK_LABEL,
        '',
        ...breedAverageCells,
        ...emptyInbreeding,
      ]);
    }

    const csv = [header, ...rows]
      .map((row) => row.map(csvEscape).join(','))
      .join('\n');
    const blob = new Blob(['\uFEFF' + csv], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `mating_predictor_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  }, [
    visibleEbvs,
    data.results,
    sireMap,
    damMap,
    data.breedAverages,
    currencySymbol,
  ]);

  return (
    <Button
      variant="outlined"
      size="small"
      startIcon={<FileDownloadOutlinedIcon />}
      onClick={handleExportCsv}
      sx={{
        flexShrink: 0,
        whiteSpace: 'nowrap',
        height: { xs: 32, md: 36 },
        px: { xs: 1, md: 1.5 },
        minWidth: 0,
        '& .MuiButton-startIcon': { mr: { xs: 0.5, md: 1 } },
      }}
    >
      Export CSV
    </Button>
  );
}
